import {
  FRAME_STYLE,
  NOTEBOOK_PAGE_GAP,
  NOTEBOOK_PAGE_HEIGHT,
  NOTEBOOK_PAGE_WIDTH,
  NOTEBOOK_STYLE,
  THEME,
  applyDarkModeFilter,
  throttleRAF,
} from "@excalidraw/common";
import { isElementLink } from "@excalidraw/element";
import { createPlaceholderEmbeddableLabel } from "@excalidraw/element";
import { getBoundTextElement } from "@excalidraw/element";
import {
  isEmbeddableElement,
  isIframeLikeElement,
  isTextElement,
} from "@excalidraw/element";
import {
  elementOverlapsWithFrame,
  getTargetFrame,
  shouldApplyFrameClip,
} from "@excalidraw/element";

import { renderElement } from "@excalidraw/element";

import { getElementAbsoluteCoords } from "@excalidraw/element";

import type {
  ElementsMap,
  ExcalidrawFrameLikeElement,
  NonDeletedExcalidrawElement,
} from "@excalidraw/element/types";

import {
  EXTERNAL_LINK_IMG,
  ELEMENT_LINK_IMG,
  getLinkHandleFromCoords,
} from "../components/hyperlink/helpers";

import { bootstrapCanvas, getNormalizedCanvasDimensions } from "./helpers";

import type {
  StaticCanvasRenderConfig,
  StaticSceneRenderConfig,
} from "../scene/types";
import type { GridStyle, StaticCanvasAppState, Zoom } from "../types";

const clampAlpha = (a: number) => Math.max(0, Math.min(1, a));

const strokeGrid = (
  context: CanvasRenderingContext2D,
  /** grid cell pixel size */
  gridSize: number,
  /** setting to 1 will disble bold lines */
  gridStep: number,
  scrollX: number,
  scrollY: number,
  zoom: Zoom,
  width: number,
  height: number,
  /** "grid" = squares (H+V), "lines" = horizontal only, "dots" = dots */
  gridStyle: GridStyle,
  /** grid line/dot color (any CSS color) */
  gridColor: string,
  /** grid opacity, 0–100 */
  gridOpacity: number,
) => {
  const offsetX = (scrollX % gridSize) - gridSize;
  const offsetY = (scrollY % gridSize) - gridSize;

  const actualGridSize = gridSize * zoom.value;

  const spaceWidth = 1 / zoom.value;

  const xEnd = offsetX + width + gridSize * 2;
  const yEnd = offsetY + height + gridSize * 2;

  // The chosen color is drawn solid; opacity is applied via globalAlpha so any
  // CSS color works. Bold steps / dots get a touch more alpha to read clearly.
  const baseAlpha = clampAlpha(gridOpacity / 100);
  const regularAlpha = baseAlpha;
  const boldAlpha = clampAlpha(baseAlpha * 1.7);
  const dotAlpha = clampAlpha(baseAlpha * 1.3);
  const dotBoldAlpha = clampAlpha(baseAlpha * 1.9);

  const isBoldX = (x: number) =>
    gridStep > 1 && Math.round(x - scrollX) % (gridStep * gridSize) === 0;
  const isBoldY = (y: number) =>
    gridStep > 1 && Math.round(y - scrollY) % (gridStep * gridSize) === 0;

  context.save();

  // Offset rendering by 0.5 to ensure that 1px wide lines are crisp.
  // We only do this when zoomed to 100% because otherwise the offset is
  // fractional, and also visibly offsets the elements.
  // We also do this per-axis, as each axis may already be offset by 0.5.
  if (zoom.value === 1) {
    context.translate(offsetX % 1 ? 0 : 0.5, offsetY % 1 ? 0 : 0.5);
  }

  // Dots style: a dot at every grid intersection (bigger/darker on bold steps).
  if (gridStyle === "dots") {
    for (let x = offsetX; x < xEnd; x += gridSize) {
      const xBold = isBoldX(x);
      if (!xBold && actualGridSize < 10) {
        continue;
      }
      for (let y = offsetY; y < yEnd; y += gridSize) {
        const yBold = isBoldY(y);
        if (!yBold && actualGridSize < 10) {
          continue;
        }
        const bold = xBold && yBold;
        const radius = (bold ? 1.9 : 1.25) / zoom.value;
        context.beginPath();
        context.globalAlpha = bold ? dotBoldAlpha : dotAlpha;
        context.fillStyle = gridColor;
        context.arc(x, y, radius, 0, Math.PI * 2);
        context.fill();
      }
    }
    context.restore();
    return;
  }

  const strokeLine = (
    isBold: boolean,
    x1: number,
    y1: number,
    x2: number,
    y2: number,
  ) => {
    const lineWidth = Math.min(1 / zoom.value, isBold ? 4 : 1);
    context.lineWidth = lineWidth;
    const lineDash = [lineWidth * 3, spaceWidth + (lineWidth + spaceWidth)];
    context.beginPath();
    context.setLineDash(isBold ? [] : lineDash);
    context.globalAlpha = isBold ? boldAlpha : regularAlpha;
    context.strokeStyle = gridColor;
    context.moveTo(x1, y1);
    context.lineTo(x2, y2);
    context.stroke();
  };

  // Vertical lines — only for the "grid" (squares) style.
  if (gridStyle === "grid") {
    for (let x = offsetX; x < xEnd; x += gridSize) {
      const isBold = isBoldX(x);
      if (!isBold && actualGridSize < 10) {
        continue;
      }
      strokeLine(isBold, x, offsetY - gridSize, x, Math.ceil(yEnd));
    }
  }

  // Horizontal lines — for both "grid" and "lines" (ruled) styles.
  for (let y = offsetY; y < yEnd; y += gridSize) {
    const isBold = isBoldY(y);
    if (!isBold && actualGridSize < 10) {
      continue;
    }
    strokeLine(isBold, offsetX - gridSize, y, Math.ceil(xEnd), y);
  }

  context.restore();
};

// PizzarWeb: paint the "notebook" surface — a vertical stack of A4 pages drawn
// directly onto the canvas surface (in world coordinates, behind every element),
// NOT as elements. This replaces the infinite grid when notebookMode is on; the
// workspace behind the pages is the (gray) viewBackgroundColor painted earlier.
const renderNotebookPages = (
  context: CanvasRenderingContext2D,
  appState: StaticCanvasAppState,
  /** viewport height in world units (normalizedHeight / zoom) */
  height: number,
) => {
  const { scrollX, scrollY, zoom } = appState;
  const dark = appState.theme === THEME.DARK;
  const filter = (c: string) => (dark ? applyDarkModeFilter(c) : c);
  const pageFill = filter(NOTEBOOK_STYLE.pageColor);
  const borderColor = filter(NOTEBOOK_STYLE.pageBorder);
  const patternColor = filter(NOTEBOOK_STYLE.patternColor);

  const pageCount = Math.max(1, Math.floor(appState.notebookPageCount || 1));
  const style = appState.notebookPageStyle ?? "ruled";
  const patternAlpha = clampAlpha((appState.notebookPageOpacity ?? 100) / 100);
  const stride = NOTEBOOK_PAGE_HEIGHT + NOTEBOOK_PAGE_GAP;

  // Only paint pages intersecting the viewport (world Y in [-scrollY, -scrollY+height]).
  const first = Math.max(
    0,
    Math.floor((-scrollY - NOTEBOOK_PAGE_GAP) / stride),
  );
  const last = Math.min(pageCount - 1, Math.ceil((-scrollY + height) / stride));

  const lw = 1 / zoom.value;
  const pad = NOTEBOOK_STYLE.padding;

  context.save();
  context.translate(scrollX, scrollY);

  for (let i = first; i <= last; i++) {
    const x = 0;
    const y = i * stride;

    // White page with a soft drop shadow.
    context.save();
    context.shadowColor = NOTEBOOK_STYLE.pageShadow;
    context.shadowBlur = 18 / zoom.value;
    context.shadowOffsetY = 8 / zoom.value;
    context.fillStyle = pageFill;
    context.fillRect(x, y, NOTEBOOK_PAGE_WIDTH, NOTEBOOK_PAGE_HEIGHT);
    context.restore();

    // Per-page pattern, clipped to the page rect.
    if (style !== "blank" && patternAlpha > 0) {
      context.save();
      context.beginPath();
      context.rect(x, y, NOTEBOOK_PAGE_WIDTH, NOTEBOOK_PAGE_HEIGHT);
      context.clip();
      context.globalAlpha = patternAlpha;
      context.strokeStyle = patternColor;
      context.fillStyle = patternColor;
      context.lineWidth = lw;

      if (style === "ruled") {
        for (
          let ly = y + pad;
          ly < y + NOTEBOOK_PAGE_HEIGHT;
          ly += NOTEBOOK_STYLE.lineGap
        ) {
          context.beginPath();
          context.moveTo(x + pad / 2, ly);
          context.lineTo(x + NOTEBOOK_PAGE_WIDTH - pad / 2, ly);
          context.stroke();
        }
      } else if (style === "grid") {
        const g = NOTEBOOK_STYLE.gridGap;
        for (
          let gx = x + pad;
          gx < x + NOTEBOOK_PAGE_WIDTH - pad / 2;
          gx += g
        ) {
          context.beginPath();
          context.moveTo(gx, y + pad);
          context.lineTo(gx, y + NOTEBOOK_PAGE_HEIGHT - pad);
          context.stroke();
        }
        for (
          let gy = y + pad;
          gy < y + NOTEBOOK_PAGE_HEIGHT - pad / 2;
          gy += g
        ) {
          context.beginPath();
          context.moveTo(x + pad, gy);
          context.lineTo(x + NOTEBOOK_PAGE_WIDTH - pad, gy);
          context.stroke();
        }
      } else if (style === "dotted") {
        const g = NOTEBOOK_STYLE.dotGap;
        const r = 1.2 / zoom.value;
        for (
          let gx = x + pad;
          gx < x + NOTEBOOK_PAGE_WIDTH - pad / 2;
          gx += g
        ) {
          for (
            let gy = y + pad;
            gy < y + NOTEBOOK_PAGE_HEIGHT - pad / 2;
            gy += g
          ) {
            context.beginPath();
            context.arc(gx, gy, r, 0, Math.PI * 2);
            context.fill();
          }
        }
      }
      context.restore();
    }

    // Page border on top of the pattern.
    context.strokeStyle = borderColor;
    context.lineWidth = lw;
    context.strokeRect(x, y, NOTEBOOK_PAGE_WIDTH, NOTEBOOK_PAGE_HEIGHT);
  }

  context.restore();
};

export const frameClip = (
  frame: ExcalidrawFrameLikeElement,
  context: CanvasRenderingContext2D,
  renderConfig: StaticCanvasRenderConfig,
  appState: StaticCanvasAppState,
) => {
  context.translate(frame.x + appState.scrollX, frame.y + appState.scrollY);
  context.beginPath();
  if (context.roundRect) {
    context.roundRect(
      0,
      0,
      frame.width,
      frame.height,
      FRAME_STYLE.radius / appState.zoom.value,
    );
  } else {
    context.rect(0, 0, frame.width, frame.height);
  }
  context.clip();
  context.translate(
    -(frame.x + appState.scrollX),
    -(frame.y + appState.scrollY),
  );
};

type LinkIconCanvas = HTMLCanvasElement & { zoom: number };

const linkIconCanvasCache: {
  regularLink: LinkIconCanvas | null;
  elementLink: LinkIconCanvas | null;
} = {
  regularLink: null,
  elementLink: null,
};

const renderLinkIcon = (
  element: NonDeletedExcalidrawElement,
  context: CanvasRenderingContext2D,
  appState: StaticCanvasAppState,
  elementsMap: ElementsMap,
) => {
  if (element.link && !appState.selectedElementIds[element.id]) {
    const [x1, y1, x2, y2] = getElementAbsoluteCoords(element, elementsMap);
    const [x, y, width, height] = getLinkHandleFromCoords(
      [x1, y1, x2, y2],
      element.angle,
      appState,
    );
    const centerX = x + width / 2;
    const centerY = y + height / 2;
    context.save();
    context.translate(appState.scrollX + centerX, appState.scrollY + centerY);
    context.rotate(element.angle);

    const canvasKey = isElementLink(element.link)
      ? "elementLink"
      : "regularLink";

    let linkCanvas = linkIconCanvasCache[canvasKey];

    if (!linkCanvas || linkCanvas.zoom !== appState.zoom.value) {
      linkCanvas = Object.assign(document.createElement("canvas"), {
        zoom: appState.zoom.value,
      });
      linkCanvas.width = width * window.devicePixelRatio * appState.zoom.value;
      linkCanvas.height =
        height * window.devicePixelRatio * appState.zoom.value;
      linkIconCanvasCache[canvasKey] = linkCanvas;

      const linkCanvasCacheContext = linkCanvas.getContext("2d")!;
      linkCanvasCacheContext.scale(
        window.devicePixelRatio * appState.zoom.value,
        window.devicePixelRatio * appState.zoom.value,
      );
      linkCanvasCacheContext.fillStyle = appState.viewBackgroundColor || "#fff";
      linkCanvasCacheContext.fillRect(0, 0, width, height);

      if (canvasKey === "elementLink") {
        linkCanvasCacheContext.drawImage(ELEMENT_LINK_IMG, 0, 0, width, height);
      } else {
        linkCanvasCacheContext.drawImage(
          EXTERNAL_LINK_IMG,
          0,
          0,
          width,
          height,
        );
      }

      linkCanvasCacheContext.restore();
    }
    context.globalAlpha = element.opacity / 100;
    context.drawImage(linkCanvas, x - centerX, y - centerY, width, height);
    context.restore();
  }
};
const _renderStaticScene = ({
  canvas,
  rc,
  elementsMap,
  allElementsMap,
  visibleElements,
  scale,
  appState,
  renderConfig,
}: StaticSceneRenderConfig) => {
  if (canvas === null) {
    return;
  }

  const { renderGrid = true, isExporting } = renderConfig;

  const [normalizedWidth, normalizedHeight] = getNormalizedCanvasDimensions(
    canvas,
    scale,
  );

  const context = bootstrapCanvas({
    canvas,
    scale,
    normalizedWidth,
    normalizedHeight,
    theme: appState.theme,
    isExporting,
    viewBackgroundColor: appState.viewBackgroundColor,
  });

  // Apply zoom
  context.scale(appState.zoom.value, appState.zoom.value);

  // Surface: notebook pages (PizzarWeb) OR the infinite grid. Notebook mode
  // replaces the grid entirely — the pages ARE the surface. Skipped on export
  // (like the grid), where the plain viewBackgroundColor is used.
  if (appState.notebookMode && !isExporting) {
    renderNotebookPages(
      context,
      appState,
      normalizedHeight / appState.zoom.value,
    );
  } else if (renderGrid) {
    strokeGrid(
      context,
      appState.gridSize,
      appState.gridStep,
      appState.scrollX,
      appState.scrollY,
      appState.zoom,
      normalizedWidth / appState.zoom.value,
      normalizedHeight / appState.zoom.value,
      appState.gridStyle,
      appState.gridColor,
      appState.gridOpacity,
    );
  }

  const groupsToBeAddedToFrame = new Set<string>();

  visibleElements.forEach((element) => {
    if (
      element.groupIds.length > 0 &&
      appState.frameToHighlight &&
      appState.selectedElementIds[element.id] &&
      (elementOverlapsWithFrame(
        element,
        appState.frameToHighlight,
        elementsMap,
      ) ||
        element.groupIds.find((groupId) => groupsToBeAddedToFrame.has(groupId)))
    ) {
      element.groupIds.forEach((groupId) =>
        groupsToBeAddedToFrame.add(groupId),
      );
    }
  });

  const inFrameGroupsMap = new Map<string, boolean>();

  // Paint visible elements
  visibleElements
    .filter((el) => !isIframeLikeElement(el))
    .forEach((element) => {
      try {
        const frameId = element.frameId || appState.frameToHighlight?.id;

        if (
          isTextElement(element) &&
          element.containerId &&
          elementsMap.has(element.containerId)
        ) {
          // will be rendered with the container
          return;
        }

        context.save();

        if (
          frameId &&
          appState.frameRendering.enabled &&
          appState.frameRendering.clip
        ) {
          const frame = getTargetFrame(element, elementsMap, appState);
          if (
            frame &&
            shouldApplyFrameClip(
              element,
              frame,
              appState,
              elementsMap,
              inFrameGroupsMap,
            )
          ) {
            frameClip(frame, context, renderConfig, appState);
          }
          renderElement(
            element,
            elementsMap,
            allElementsMap,
            rc,
            context,
            renderConfig,
            appState,
          );
        } else {
          renderElement(
            element,
            elementsMap,
            allElementsMap,
            rc,
            context,
            renderConfig,
            appState,
          );
        }

        const boundTextElement = getBoundTextElement(element, elementsMap);
        if (boundTextElement) {
          renderElement(
            boundTextElement,
            elementsMap,
            allElementsMap,
            rc,
            context,
            renderConfig,
            appState,
          );
        }

        context.restore();

        if (!isExporting) {
          renderLinkIcon(element, context, appState, elementsMap);
        }
      } catch (error: any) {
        console.error(
          error,
          element.id,
          element.x,
          element.y,
          element.width,
          element.height,
        );
      }
    });

  // render embeddables on top
  visibleElements
    .filter((el) => isIframeLikeElement(el))
    .forEach((element) => {
      try {
        const render = () => {
          renderElement(
            element,
            elementsMap,
            allElementsMap,
            rc,
            context,
            renderConfig,
            appState,
          );

          if (
            isIframeLikeElement(element) &&
            (isExporting ||
              (isEmbeddableElement(element) &&
                renderConfig.embedsValidationStatus.get(element.id) !==
                  true)) &&
            element.width &&
            element.height
          ) {
            const label = createPlaceholderEmbeddableLabel(element);
            renderElement(
              label,
              elementsMap,
              allElementsMap,
              rc,
              context,
              renderConfig,
              appState,
            );
          }
          if (!isExporting) {
            renderLinkIcon(element, context, appState, elementsMap);
          }
        };
        // - when exporting the whole canvas, we DO NOT apply clipping
        // - when we are exporting a particular frame, apply clipping
        //   if the containing frame is not selected, apply clipping
        const frameId = element.frameId || appState.frameToHighlight?.id;

        if (
          frameId &&
          appState.frameRendering.enabled &&
          appState.frameRendering.clip
        ) {
          context.save();

          const frame = getTargetFrame(element, elementsMap, appState);

          if (
            frame &&
            shouldApplyFrameClip(
              element,
              frame,
              appState,
              elementsMap,
              inFrameGroupsMap,
            )
          ) {
            frameClip(frame, context, renderConfig, appState);
          }
          render();
          context.restore();
        } else {
          render();
        }
      } catch (error: any) {
        console.error(error);
      }
    });

  // render pending nodes for flowcharts
  renderConfig.pendingFlowchartNodes?.forEach((element) => {
    try {
      renderElement(
        element,
        elementsMap,
        allElementsMap,
        rc,
        context,
        renderConfig,
        appState,
      );
    } catch (error) {
      console.error(error);
    }
  });
};

/** throttled to animation framerate */
export const renderStaticSceneThrottled = throttleRAF(
  (config: StaticSceneRenderConfig) => {
    _renderStaticScene(config);
  },
);

/**
 * Static scene is the non-ui canvas where we render elements.
 */
export const renderStaticScene = (
  renderConfig: StaticSceneRenderConfig,
  throttle?: boolean,
) => {
  if (throttle) {
    renderStaticSceneThrottled(renderConfig);
    return;
  }

  _renderStaticScene(renderConfig);
};
